import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, AlertTriangle, Search, User, X } from 'lucide-react';
import { Combobox } from './ui/Combobox';
import { useAuth } from '../context/AuthContext';
import { rolService } from '../services/rolService';
import { lumisialService } from '../services/lumisialService';
import { diocesisService } from '../services/diocesisService';
import { provinciaEclesiasticaService } from '../services/provinciaEclesiasticaService';
import { solicitudRolService } from '../services/solicitudRolService';
import { miembroService } from '../services/miembroService';
import { Rol } from '../types/auth';
import { Lumisial, Diocesis, ProvinciaEclesiastica, Miembro } from '../lib/supabase';
import { DialogShell } from './ui/DialogShell';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { getRolesCanRequest, normalizeRoleName, getRoleScope } from '../utils/roleHierarchy';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { TextInput } from './ui/TextInput';
import { Select } from './ui/Select';
import { formatJerarquiaMiembro, difiereJerarquia } from '../utils/miembroJerarquia';
import {
  FORM_BORDER,
  FORM_BORDER_HOVER,
  FORM_BORDER_ERROR,
  FOCUS_RING,
  FOCUS_RING_ERROR,
  FORM_RADIUS,
  LABEL_TEXT,
  ERROR_TEXT,
} from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

/** Campos del formulario que participan de la validación. */
interface DatosSolicitud {
  readonly requested_role_id: string;
  readonly provincia_eclesiastica_id: string;
  readonly diocesis_id: string;
  readonly lumisial_id: string;
}

type CampoMiembroEnRojo = 'sin-seleccion' | 'sin-correo';

type ResultadoValidacion =
  | { readonly ok: false; readonly mensaje: string; readonly campoMiembro?: CampoMiembroEnRojo }
  | { readonly ok: true; readonly scope: ReturnType<typeof getRoleScope>; readonly miembro: Miembro };

/**
 * Valida la solicitud antes de enviarla. Vive fuera del componente para que `handleSubmit`
 * quede legible (era una escalera de ocho `if` con su propio `setLoading(false); return;`)
 * y para poder probar las reglas sin montar el modal.
 */
function validarSolicitud(
  rolesDisponibles: Rol[],
  formData: DatosSolicitud,
  selectedMiembro: Miembro | null
): ResultadoValidacion {
  const rolSeleccionado = rolesDisponibles.find((r) => r.id === formData.requested_role_id);
  if (!rolSeleccionado) return { ok: false, mensaje: 'Debe seleccionar un rol válido' };

  const normalized = normalizeRoleName(rolSeleccionado.nombre);
  if (!normalized) return { ok: false, mensaje: 'Rol no válido' };

  const scope = getRoleScope(normalized);
  const faltaGeografia: Partial<Record<typeof scope, { readonly valor: string; readonly mensaje: string }>> = {
    provincia: { valor: formData.provincia_eclesiastica_id, mensaje: 'Debe seleccionar una provincia eclesiástica' },
    diocesis: { valor: formData.diocesis_id, mensaje: 'Debe seleccionar una diócesis' },
    lumisial: { valor: formData.lumisial_id, mensaje: 'Debe seleccionar un lumisial' },
  };
  const requerido = faltaGeografia[scope];
  if (requerido && !requerido.valor) return { ok: false, mensaje: requerido.mensaje };

  if (!selectedMiembro) {
    return { ok: false, mensaje: 'Debe seleccionar un miembro', campoMiembro: 'sin-seleccion' };
  }

  // Sin correo no se le puede crear la cuenta al aprobar. Se corta acá: antes esto se descubría
  // cuando el admin superior intentaba aprobar, y había que devolverle la solicitud por algo que
  // quien la pide puede arreglar en un minuto.
  if (!selectedMiembro.email?.trim()) {
    return {
      ok: false,
      mensaje:
        `${selectedMiembro.nombres} ${selectedMiembro.apellidos} no tiene correo registrado. ` +
        'Agrégalo en su ficha antes de solicitar el rol: sin correo no se le puede crear la cuenta.',
      campoMiembro: 'sin-correo',
    };
  }

  return { ok: true, scope, miembro: selectedMiembro };
}

interface SolicitarRolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Modo "Modificar Rol": miembro fijo (sin buscador), título distinto,
   *  y su rol actual excluido del dropdown. */
  miembroPreseleccionado?: {
    id: string;
    rolActualId: string;
    rolActualNombre: string;
  } | null;
}

export const SolicitarRolModal: React.FC<SolicitarRolModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  miembroPreseleccionado,
}) => {
  const { usuario } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [rolesDisponibles, setRolesDisponibles] = useState<Rol[]>([]);
  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<ProvinciaEclesiastica[]>([]);
  const [diocesisList, setDiocesisList] = useState<Diocesis[]>([]);
  const [lumisiales, setLumisiales] = useState<Lumisial[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Miembro[]>([]);
  const [selectedMiembro, setSelectedMiembro] = useState<Miembro | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  // 'sin-seleccion' pinta el borde y explica bajo el campo; 'sin-correo' solo pinta el borde,
  // porque el aviso de arriba ya lo explica con el nombre de la persona y no hay que decirlo dos veces.
  const [memberFieldError, setMemberFieldError] = useState<CampoMiembroEnRojo | null>(null);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  const [formData, setFormData] = useState({
    requested_role_id: '',
    provincia_eclesiastica_id: '',
    diocesis_id: '',
    lumisial_id: '',
    reason: '',
  });

  const esModificar = !!miembroPreseleccionado;

  const loadRolesDisponibles = useCallback(async () => {
    if (!usuario) return;
    try {
      const todosRoles = await rolService.getAll();
      const rolesQuePuedoSolicitar = getRolesCanRequest(usuario.rol.nombre);

      const rolesFiltrados = todosRoles.filter((rol) => {
        const normalized = normalizeRoleName(rol.nombre);
        return normalized && rolesQuePuedoSolicitar.includes(normalized);
      });

      setRolesDisponibles(rolesFiltrados);
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos cargar los roles disponibles.');
    }
  }, [usuario]);

  const loadProvinciasEclesiasticas = useCallback(async () => {
    try {
      const data = await provinciaEclesiasticaService.listarActivos();
      setProvinciasEclesiasticas(data);
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos cargar las provincias eclesiásticas.');
    }
  }, []);

  useEffect(() => {
    if (usuario) {
      loadRolesDisponibles();
      loadProvinciasEclesiasticas();
    }
  }, [usuario, loadRolesDisponibles, loadProvinciasEclesiasticas]);

  useEffect(() => {
    const searchMiembros = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setSearchLoading(true);
      try {
        const results = await miembroService.search(searchTerm, 10);
        setSearchResults(results);
        setShowResults(true);
      } catch (error) {
        reportarErrorInesperado(error, 'No pudimos cargar la búsqueda de miembros.');
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(() => {
      searchMiembros();
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const loadDiocesisPorProvinciaEclesiastica = async (provinciaEclesiasticaId: string) => {
    try {
      const data = await diocesisService.listarPorProvinciaEclesiastica(provinciaEclesiasticaId);
      setDiocesisList(data);
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos cargar las diócesis.');
    }
  };

  const loadLumisialesPorDiocesis = async (diocesisId: string) => {
    try {
      const allLumisiales = await lumisialService.getAll();
      const filtered = allLumisiales.filter((l) => l.diocesis_id === diocesisId);
      setLumisiales(filtered);
    } catch (error) {
      reportarErrorInesperado(error, 'No pudimos cargar los lumisiales.');
    }
  };

  // Se dispara junto con el cambio de provincia_eclesiastica_id en cada handler que lo
  // establece (miembro, rol, Combobox), en vez de reaccionar vía useEffect a la
  // actualización de formData (react-doctor/no-chain-state-updates).
  const actualizarProvincia = (provinciaEclesiasticaId: string) => {
    if (provinciaEclesiasticaId) {
      loadDiocesisPorProvinciaEclesiastica(provinciaEclesiasticaId);
    } else {
      setDiocesisList([]);
      setLumisiales([]);
    }
  };

  // Idem para diocesis_id → lumisiales.
  const actualizarDiocesis = (diocesisId: string) => {
    if (diocesisId) {
      loadLumisialesPorDiocesis(diocesisId);
    } else {
      setLumisiales([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuario) return;

    handleError('');
    setMemberFieldError(null);
    setLoading(true);

    try {
      const validacion = validarSolicitud(rolesDisponibles, formData, selectedMiembro);
      if (!validacion.ok) {
        handleError(validacion.mensaje);
        if (validacion.campoMiembro) setMemberFieldError(validacion.campoMiembro);
        setLoading(false);
        return;
      }
      const { scope, miembro } = validacion;

      await solicitudRolService.create({
        miembro_id: miembro.id,
        rol_solicitado_id: formData.requested_role_id,
        lumisial_id: scope === 'lumisial' ? formData.lumisial_id : undefined,
        diocesis_id: scope === 'diocesis' || scope === 'lumisial' ? formData.diocesis_id : undefined,
        provincia_eclesiastica_id: formData.provincia_eclesiastica_id || undefined,
        motivo: formData.reason,
      });

      resetForm();

      toast.success('Solicitud enviada exitosamente. Será revisada por un administrador.');

      onSuccess();
      onClose();
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Error al crear la solicitud';
      reportarErrorInesperado(e, errorMessage);

    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      requested_role_id: '',
      provincia_eclesiastica_id: '',
      diocesis_id: '',
      lumisial_id: '',
      reason: '',
    });
    setSelectedMiembro(null);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
    setMemberFieldError(null);
    setDiocesisList([]);
    setLumisiales([]);
    handleError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const hasFormChanges = (): boolean =>
    esModificar
      ? !!formData.requested_role_id || formData.reason.trim() !== ''
      : (
          !!selectedMiembro ||
          !!formData.requested_role_id ||
          !!formData.provincia_eclesiastica_id ||
          !!formData.diocesis_id ||
          !!formData.lumisial_id ||
          formData.reason.trim() !== '' ||
          searchTerm.trim() !== ''
        );

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  const rolesOfrecidos = miembroPreseleccionado
    ? rolesDisponibles.filter((r) => r.id !== miembroPreseleccionado.rolActualId)
    : rolesDisponibles;

  const rolSeleccionado = rolesOfrecidos.find((r) => r.id === formData.requested_role_id);
  const normalized = rolSeleccionado ? normalizeRoleName(rolSeleccionado.nombre) : null;
  const scope = normalized ? getRoleScope(normalized) : null;

  const necesitaProvincia = scope === 'provincia' || scope === 'diocesis' || scope === 'lumisial';
  const necesitaDiocesis = scope === 'diocesis' || scope === 'lumisial';
  const necesitaLumisial = scope === 'lumisial';

  const jerarquiaRealMiembro = selectedMiembro?.lumisial
    ? {
        provincia_eclesiastica_id: selectedMiembro.lumisial.provincia_eclesiastica_id ?? null,
        diocesis_id: selectedMiembro.lumisial.diocesis_id ?? null,
        lumisial_id: selectedMiembro.lumisial_actual_id ?? null,
      }
    : null;

  const hayDiscrepanciaJerarquia =
    !!jerarquiaRealMiembro &&
    difiereJerarquia(
      {
        provincia_eclesiastica_id: formData.provincia_eclesiastica_id || null,
        diocesis_id: formData.diocesis_id || null,
        lumisial_id: formData.lumisial_id || null,
      },
      jerarquiaRealMiembro
    );

  const handleSelectMiembro = (miembro: Miembro) => {
    setSelectedMiembro(miembro);
    setSearchTerm('');
    setShowResults(false);
    setMemberFieldError(null);

    if (miembro.lumisial) {
      const provinciaId = miembro.lumisial.provincia_eclesiastica_id || '';
      const diocesisId = miembro.lumisial.diocesis_id || '';
      setFormData((prev) => ({
        ...prev,
        provincia_eclesiastica_id: provinciaId,
        diocesis_id: diocesisId,
        lumisial_id: miembro.lumisial_actual_id || '',
      }));
      actualizarProvincia(provinciaId);
      actualizarDiocesis(diocesisId);
    }
  };

  useEffect(() => {
    if (!isOpen || !miembroPreseleccionado) return;
    let cancelado = false;
    miembroService.getByIdConJerarquia(miembroPreseleccionado.id)
      .then((miembro) => {
        if (miembro && !cancelado) handleSelectMiembro(miembro);
      })
      .catch((error) => { console.error('Error cargando miembro:', error); });
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, miembroPreseleccionado]);

  const handleClearMiembro = () => {
    setSelectedMiembro(null);
    setSearchTerm('');
    setFormData((prev) => ({
      ...prev,
      provincia_eclesiastica_id: '',
      diocesis_id: '',
      lumisial_id: '',
    }));
    setDiocesisList([]);
    setLumisiales([]);
  };

  const renderMiembroField = () => {
    if (esModificar) {
      if (!selectedMiembro) {
        return (
          <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-500"></div>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex-shrink-0 w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900">
              {selectedMiembro.nombres} {selectedMiembro.apellidos}
            </div>
            {selectedMiembro.lumisial && (
              <div className="text-xs text-slate-500 mt-1">
                {formatJerarquiaMiembro(selectedMiembro.lumisial)}
              </div>
            )}
            {miembroPreseleccionado && (
              <div className="text-xs text-slate-500 mt-1">
                Rol actual: <span className="font-medium">{miembroPreseleccionado.rolActualNombre}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (!selectedMiembro) {
      return (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            id="miembro-search-input"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, apellidos o email..."
            className={`w-full pl-10 pr-4 py-2 ${FORM_RADIUS} ${
              memberFieldError ? FORM_BORDER_ERROR : `${FORM_BORDER} ${FORM_BORDER_HOVER}`
            } ${memberFieldError ? FOCUS_RING_ERROR : FOCUS_RING}`}
          />
          {showResults && searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((miembro) => (
                <button
                  key={miembro.id}
                  type="button"
                  onClick={() => handleSelectMiembro(miembro)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {miembro.nombres} {miembro.apellidos}
                    </div>
                    {miembro.lumisial && (
                      <div className="text-xs text-slate-500 mt-1">
                        {formatJerarquiaMiembro(miembro.lumisial)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {searchLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-500"></div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="flex-shrink-0 w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center">
          <User className="w-5 h-5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900">
            {selectedMiembro.nombres} {selectedMiembro.apellidos}
          </div>
          {selectedMiembro.lumisial && (
            <div className="text-xs text-slate-500 mt-1">
              {formatJerarquiaMiembro(selectedMiembro.lumisial)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleClearMiembro}
          className="flex-shrink-0 p-1 hover:bg-slate-200 rounded"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>
      </div>
    );
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      title={esModificar ? 'Modificar Rol' : 'Solicitar Rol para Miembro'}
      hasUnsavedChanges={hasFormChanges()}
      closeRef={closeRef}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormErrorAlert error={error} onClear={clearError} />

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            {esModificar
              ? 'Seleccione el nuevo rol para este miembro. La solicitud será enviada a un administrador superior para su aprobación.'
              : 'Busque y seleccione el miembro al que desea asignar un rol en la aplicación. La solicitud será enviada a un administrador superior para su aprobación.'}
          </p>
        </div>

        <div>
          <label htmlFor="miembro-search-input" className={`block ${LABEL_TEXT} mb-1.5`}>
            Miembro<span className="text-red-500 ml-1">*</span>
          </label>
          {renderMiembroField()}
          {memberFieldError === 'sin-seleccion' && <p className={ERROR_TEXT}>Debe seleccionar un miembro</p>}
        </div>

        <div>
          <Select
            label="Rol Solicitado"
            options={rolesOfrecidos.map((rol) => ({ value: rol.id, label: rol.nombre }))}
            value={formData.requested_role_id}
            onChange={(v) => {
              const provinciaId = selectedMiembro?.lumisial?.provincia_eclesiastica_id || '';
              const diocesisId = selectedMiembro?.lumisial?.diocesis_id || '';
              setFormData({
                ...formData,
                requested_role_id: v,
                provincia_eclesiastica_id: provinciaId,
                diocesis_id: diocesisId,
                lumisial_id: selectedMiembro?.lumisial_actual_id || '',
              });
              actualizarProvincia(provinciaId);
              actualizarDiocesis(diocesisId);
            }}
            placeholder="Seleccione un rol"
            required
          />
          {rolesOfrecidos.length === 0 && (
            <p className="text-sm text-slate-500 mt-1">
              No tiene permisos para solicitar roles
            </p>
          )}
        </div>

        {necesitaProvincia && (
          <Combobox
            label="Provincia Eclesiástica"
            options={provinciasEclesiasticas.map((p) => ({
              value: p.id,
              label: p.nombre,
            }))}
            value={formData.provincia_eclesiastica_id}
            onChange={(value) => {
              setFormData({
                ...formData,
                provincia_eclesiastica_id: value,
                diocesis_id: '',
                lumisial_id: '',
              });
              actualizarProvincia(value);
              actualizarDiocesis('');
            }}
            placeholder="Buscar provincia eclesiástica..."
            required
          />
        )}

        {necesitaDiocesis && formData.provincia_eclesiastica_id && (
          <Combobox
            label="Diócesis/Prefectura"
            options={diocesisList.map((d) => ({
              value: d.id,
              label: d.nombre,
              subtitle: d.tipo === 'diocesis' ? 'Diócesis' : 'Prefectura',
            }))}
            value={formData.diocesis_id}
            onChange={(value) => {
              setFormData({
                ...formData,
                diocesis_id: value,
                lumisial_id: '',
              });
              actualizarDiocesis(value);
            }}
            placeholder="Buscar diócesis o prefectura..."
            required
          />
        )}

        {necesitaLumisial && formData.diocesis_id && (
          <Combobox
            label="Lumisial"
            options={lumisiales.map((l) => ({
              value: l.id,
              label: l.nombre,
              subtitle: l.ciudad,
            }))}
            value={formData.lumisial_id}
            onChange={(value) =>
              setFormData({ ...formData, lumisial_id: value })
            }
            placeholder="Buscar lumisial..."
            required
          />
        )}

        {hayDiscrepanciaJerarquia && (necesitaProvincia || necesitaDiocesis || necesitaLumisial) && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Distinto a la jerarquía real del miembro.
            </p>
          </div>
        )}

        <TextInput
          label={esModificar ? 'Motivo del Cambio' : 'Motivo de la Solicitud'}
          value={formData.reason}
          onChange={(v) => setFormData({ ...formData, reason: v })}
          placeholder="Presidente JAL electo en asamblea general el 29/03/2025"
          required
        />

        <div className="flex gap-3">
          <Button type="submit" disabled={loading || rolesOfrecidos.length === 0}>
            <Shield className="w-4 h-4 mr-2" />
            {loading ? 'Enviando...' : 'Enviar Solicitud'}
          </Button>
          <Button type="button" variant="outline" onClick={requestClose}>
            Cancelar
          </Button>
        </div>
      </form>
    </DialogShell>
  );
};
